"use client";

import React, { useState, useEffect, useRef } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import axiosInstance from '@/api/axios';
import { useTranslation } from 'react-i18next';
import { InfoTooltip } from '../Recycle/Infotooltip';

// production: what was actually produced given real sorting (Scenario B).
// unsorted:   what would have been produced if nothing had been sorted
//             (Scenario A / baseline) — always >= production.
// The gap between the two lines IS the CO2 savings, drawn as an implied
// area rather than its own Area layer (there's nothing to plot for a
// "gap" — it falls out naturally from unsorted being taller).
const COLORS = { production: '#10b981', unsorted: '#3b82f6' };

const WASTE_TYPES = [
  { key: 'all',      label: 'All' },
  { key: 'organic',  label: 'Organic' },
  { key: 'pmd',      label: 'PMD' },
  { key: 'paper',    label: 'Paper' },
];

const CO2SavingsCard = ({
  id = 'R6',
  wasteitem = 'organic',
  dayvalue = 'year',
  datevalue = [{ startDate: null, endDate: null, key: 'selection' }],
  selectedButton_ = 'year'
}) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWasteType, setSelectedWasteType] = useState('all');
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(500);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === 'undefined') return;
    const el = containerRef.current;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // No upper cap — everything scales continuously with card width.
  const textScale = Math.max(0.65, containerWidth / 700);
  const axisFont = Math.max(8, Math.round(10 * textScale));
  const strokeW = Math.max(1.5, 2 * textScale);
  // Widened from 30 to 44 — tick labels now include a " kg" suffix, which
  // needs more room than the bare numbers this width was originally sized for.
  const yAxisWidth = Math.max(44, Math.round(48 * textScale));
  const chartMargin = {
    top: Math.round(8 * textScale),
    right: Math.round(12 * textScale),
    left: 0,
    bottom: 0,
  };

  // FIX: previously labeled/keyed around "recycled" (a weight in kg) and
  // "savings", with a third purely decorative legend swatch that matched
  // no real series at all. Now matches the backend's report-consistent
  // Scenario A/B model: production = what was actually emitted given real
  // sorting, unsorted = the baseline if nothing had been sorted, savings =
  // the gap between them (not an independent series — see COLORS above).
  const translations = {
    chartTitle:       t('co2SavingsChart.chartTitle')       || "CO₂ Savings",
    productionLabel:  t('co2SavingsChart.productionLabel')  || "CO₂ Production",
    unsortedLabel:    t('co2SavingsChart.unsortedLabel')    || "Unsorted CO₂ Production",
    savingsLabel:     t('co2SavingsChart.savingsLabel')     || "CO₂ Savings",
    loading:          t('co2SavingsChart.loading')          || "Loading data...",
    loadError:        t('co2SavingsChart.loadError')        || "Failed to load data.",
    noData:           t('co2SavingsChart.noData')           || "No data available for selected period"
  };

  const isCustom = selectedButton_ === 'custom';

  const formatForBackend = (date) => {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    return d.toDateString();
  };

  const customDateRangeKey = (() => {
    if (!isCustom) return null;
    const r = datevalue?.[0];
    if (!r?.startDate || !r?.endDate) return null;
    const start = r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate;
    const end   = r.endDate   instanceof Date ? r.endDate.toISOString()   : r.endDate;
    return `${start}__${end}`;
  })();

  // Range badge — only shown for an actual calendar selection, built
  // straight from the prop (not a re-parsed API date), same as
  // RecyclabilityScore, so it can't drift from the picker's own timezone.
  const getRangeLabel = () => {
    if (!isCustom) return null;
    const r = datevalue?.[0];
    if (!r?.startDate || !r?.endDate) return null;
    const start = r.startDate instanceof Date ? r.startDate : new Date(r.startDate);
    const end   = r.endDate   instanceof Date ? r.endDate   : new Date(r.endDate);
    const opts  = { day: 'numeric', month: 'short' };
    return `${start.toLocaleDateString(i18n.language, opts)} – ${end.toLocaleDateString(i18n.language, { ...opts, year: 'numeric' })}`;
  };

  // FIX: previously fell back to the `wasteitem` PROP (the parent
  // dashboard's own selected waste type, default 'organic') whenever the
  // pill was set to 'all' — meaning "All" never actually queried all
  // types, it silently queried whatever the parent's dropdown happened to
  // be set to. Since that's usually 'organic', "All" and "Organic" ended
  // up sending the exact same request and showing identical charts. The
  // backend already treats waste_item == 'all' as "don't filter by type"
  // (see `if waste_item != "all": filters['waste_type'] = waste_item`),
  // so the pill's own selection can just be sent through directly.
  const resolvedWasteItem = selectedWasteType;

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        id,
        wasteitem: resolvedWasteItem,
        day: dayvalue,
        withcalendar: isCustom,
      };

      if (isCustom && datevalue?.[0]?.startDate && datevalue?.[0]?.endDate) {
        params.startDate = formatForBackend(datevalue[0].startDate);
        params.endDate   = formatForBackend(datevalue[0].endDate);
      }

      const response = await axiosInstance.get("/co2savings", { params, timeout: 10000 });

      if (!response.data) throw new Error(translations.loadError);

      // Backend now returns { production, unsorted, savings, recycled } —
      // all keyed by the same bucket labels. This chart only plots
      // production and unsorted; savings is the visual gap between them,
      // and recycled (kg, not kg CO2e) isn't shown here at all.
      let transformedData = [];
      if (response.data.production && response.data.unsorted) {
        transformedData = Object.keys(response.data.unsorted).map(key => ({
          name:       key,
          production: response.data.production[key] || 0,
          unsorted:   response.data.unsorted[key]    || 0,
        }));
      } else if (Array.isArray(response.data)) {
        transformedData = response.data.map(item => ({
          name:       item.date || item.name,
          production: item.production || 0,
          unsorted:   item.unsorted   || 0,
        }));
      }

      setChartData(transformedData);
    } catch (err) {
      console.error("API Error:", err);
      if (resolvedWasteItem !== 'pmd') setError(err.message || translations.loadError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && resolvedWasteItem && dayvalue) fetchData();
  }, [id, resolvedWasteItem, dayvalue, selectedButton_, customDateRangeKey]);

  const getButtonStyle = (key) => {
    const isActive = selectedWasteType === key;
    return `whitespace-nowrap rounded-full font-medium transition-all duration-200 border ${
      isActive
        ? 'bg-primary text-white dark:text-slate-900 border-primary shadow-md'
        : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:text-primary hover:bg-gray-50 dark:hover:bg-slate-700'
    }`;
  };

  // Label budget scales with the font size, so labels don't overlap or drop out
  const tickInterval = (() => {
    const approxLabelWidthPx = 40 * textScale;
    const maxLabels = Math.max(3, Math.floor((containerWidth - 40) / approxLabelWidthPx));
    if (chartData.length <= maxLabels) return 0;
    return Math.ceil(chartData.length / maxLabels) - 1;
  })();

  const dotSize = `${Math.max(6, Math.round(12 * textScale))}px`;
  const legendFont = `${Math.max(8, Math.round(12 * textScale))}px`;

  return (
    <div ref={containerRef} className="sm:rounded-lg bg-muted/50 p-2">
      {/* Header row: title (+ range badge + info icon) + waste type buttons */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <h2
            className="scroll-m-20 font-semibold tracking-tight"
            style={{ fontSize: `${Math.round(20 * textScale)}px` }}
          >
            {translations.chartTitle}
          </h2>
          {getRangeLabel() && (
            <span className="text-xs text-muted-foreground">{getRangeLabel()}</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Waste type selector pills — same style as SubclassCardView */}
          <div className="relative">
            <div className="flex items-center overflow-x-auto no-scrollbar pb-1 mask-linear-fade" style={{ gap: `${Math.round(8 * textScale)}px` }}>
              {WASTE_TYPES.map(({ key, label }) => (
                <button
                  key={key}
                  className={getButtonStyle(key)}
                  style={{
                    fontSize: `${Math.max(9, Math.round(12 * textScale))}px`,
                    padding: `${Math.max(3, Math.round(6 * textScale))}px ${Math.max(8, Math.round(16 * textScale))}px`,
                  }}
                  onClick={() => setSelectedWasteType(key)}
                >
                  {key === 'all' ? (t('common.all') || 'All') : (t(`wasteImpact.streams.${key}`) || label)}
                </button>
              ))}
            </div>
          </div>
          <InfoTooltip text="Compares the CO₂ actually produced by your sorted waste against what an unsorted baseline would have produced. The gap between the two lines is your CO₂ savings." />
        </div>
      </div>

      <div className="aspect-[4/3] md:aspect-[16/9]">
        <div className="flex flex-col h-full w-full gap-0">
          <div className="flex-1 min-h-0">
            {loading ? (
              // Skeleton placeholder — header, pills and legend stay real;
              // only the plot area (axes + two layered wavy areas) is
              // replaced, in the same shape as the loaded chart, so
              // nothing jumps when the real data arrives.
              <div className="h-full w-full flex motion-safe:animate-pulse" aria-busy="true">
                <span className="sr-only" role="status">{translations.loading}</span>

                {/* Y-axis ticks */}
                <div className="flex flex-col justify-between py-2 pr-2" style={{ width: yAxisWidth }}>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-2 w-3/4 ml-auto rounded bg-gray-200 dark:bg-slate-700" />
                  ))}
                </div>

                <div className="flex-1 flex flex-col min-w-0">
                  <div className="relative flex-1">
                    {/* Grid lines */}
                    <div className="absolute inset-0 flex flex-col justify-between py-2">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <div key={i} className="h-px w-full bg-gray-200 dark:bg-slate-700" />
                      ))}
                    </div>
                    {/* Two layered areas — unsorted behind, production in front */}
                    <svg
                      viewBox="0 0 100 40"
                      preserveAspectRatio="none"
                      className="absolute inset-0 w-full h-full"
                      aria-hidden="true"
                    >
                      <path
                        d="M0,28 C10,22 20,26 30,18 C40,10 50,20 60,15 C70,10 80,18 90,12 L100,10 L100,40 L0,40 Z"
                        className="fill-gray-200 dark:fill-slate-700"
                      />
                      <path
                        d="M0,34 C10,30 20,33 30,27 C40,22 50,29 60,25 C70,21 80,27 90,23 L100,22 L100,40 L0,40 Z"
                        className="fill-gray-300 dark:fill-slate-600"
                      />
                    </svg>
                  </div>

                  {/* X-axis ticks */}
                  <div className="flex justify-between pt-2 px-1">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="h-2 w-8 rounded bg-gray-200 dark:bg-slate-700" />
                    ))}
                  </div>
                </div>
              </div>
            ) : error && resolvedWasteItem !== 'pmd' ? (
              <div className="h-full flex items-center justify-center p-4">
                <p className="text-red-500">{error}</p>
              </div>
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={chartMargin}>
                  <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="name"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: axisFont }}
                    interval={tickInterval}
                    minTickGap={8}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: axisFont }}
                    width={yAxisWidth}
                    domain={[0, (dataMax) => dataMax * 1.1]}
                    // FIX: with no formatter, Recharts falls back to its own
                    // default number formatting for auto-generated ticks —
                    // on a small-magnitude domain like this (values well
                    // under 1) that can render a tick as something like
                    // "0.00003", which then gets visually clipped by the
                    // narrow axis width down to just "00003". Formatting
                    // explicitly avoids relying on Recharts' guess. Scales
                    // decimal places to the data's own magnitude, same
                    // approach Stats.tsx already uses (formatValue).
                    tickFormatter={(value) => {
                      const abs = Math.abs(value);
                      if (abs === 0) return '0 kg';
                      if (abs < 0.01) return `${value.toFixed(4)} kg`;
                      if (abs < 1)    return `${value.toFixed(2)} kg`;
                      return `${value.toFixed(1)} kg`;
                    }}
                  />
                  {/*
                    "unsorted" drawn first so it renders BEHIND "production"
                    (later JSX = drawn on top in Recharts). unsorted is
                    always >= production by construction (production =
                    unsorted - savings, floored at 0), so the sliver of
                    unsorted's fill still visible above production's solid
                    fill IS the savings — no separate Area needed for it.
                  */}
                  <Area type="monotone" dataKey="unsorted"   stroke={COLORS.unsorted}   fill={COLORS.unsorted}   fillOpacity={0.25} strokeWidth={strokeW} />
                  <Area type="monotone" dataKey="production" stroke={COLORS.production} fill={COLORS.production} fillOpacity={0.55} strokeWidth={strokeW} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-500">{translations.noData}</p>
              </div>
            )}
          </div>

          {/* Legend — wraps instead of clipping on narrow cards.
              Third swatch now legitimately describes the visual gap
              between the two real Area layers, rather than a phantom
              series like before. */}
          <div className="flex justify-center mt-2">
            <div
              className="flex flex-wrap items-center justify-center"
              style={{ gap: `${Math.round(6 * textScale)}px ${Math.round(16 * textScale)}px` }}
            >
              <div className="flex items-center" style={{ gap: `${Math.round(4 * textScale)}px` }}>
                <div className="rounded-full flex-shrink-0" style={{ backgroundColor: COLORS.production, width: dotSize, height: dotSize }} />
                <span className="font-medium whitespace-nowrap" style={{ fontSize: legendFont }}>{translations.productionLabel}</span>
              </div>
              <div className="flex items-center" style={{ gap: `${Math.round(4 * textScale)}px` }}>
                <div style={{ backgroundColor: COLORS.unsorted, width: dotSize, height: '2px' }} />
                <span className="font-medium whitespace-nowrap" style={{ fontSize: legendFont }}>{translations.unsortedLabel}</span>
              </div>
              <div className="flex items-center" style={{ gap: `${Math.round(4 * textScale)}px` }}>
                <div className="rounded-full opacity-25 flex-shrink-0" style={{ backgroundColor: COLORS.unsorted, width: dotSize, height: dotSize }} />
                <span className="font-medium whitespace-nowrap" style={{ fontSize: legendFont }}>{translations.savingsLabel}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CO2SavingsCard;